import type { Group, Pouch, Sheet } from '../../types';
import { GROUP_COLORS } from '../../types';

export const DEFAULT_POUCH_COLOR = 'gray';
export const DEFAULT_GROUP_COLOR = GROUP_COLORS[0].id;

function isNonEmptyColor(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** 未設定グループに yellow → green → blue … と重複なく割当 */
export function assignGroupColors(groups: Group[]): Group[] {
  const used = new Set(
    groups.map(g => g.color).filter(isNonEmptyColor),
  );
  let cursor = 0;

  return groups.map(group => {
    if (isNonEmptyColor(group.color)) {
      return group;
    }
    while (cursor < GROUP_COLORS.length && used.has(GROUP_COLORS[cursor].id)) {
      cursor += 1;
    }
    const color =
      cursor < GROUP_COLORS.length
        ? GROUP_COLORS[cursor].id
        : GROUP_COLORS[cursor % GROUP_COLORS.length].id;
    used.add(color);
    cursor += 1;
    return { ...group, color };
  });
}

/** write 前の生 JSON にパウチ gray・グループ色を明示付与 */
export function normalizeIncomingColorFields(raw: Record<string, unknown>): void {
  if (Array.isArray(raw.pouches)) {
    for (const pouch of raw.pouches) {
      if (!isRecord(pouch)) continue;
      if (!isNonEmptyColor(pouch.color)) {
        pouch.color = DEFAULT_POUCH_COLOR;
      }
    }
  }
  if (!Array.isArray(raw.groups)) return;

  const used = new Set<string>();
  let cursor = 0;
  for (const group of raw.groups) {
    if (!isRecord(group)) continue;
    if (isNonEmptyColor(group.color)) {
      used.add(group.color);
      continue;
    }
    while (cursor < GROUP_COLORS.length && used.has(GROUP_COLORS[cursor].id)) {
      cursor += 1;
    }
    const color =
      cursor < GROUP_COLORS.length
        ? GROUP_COLORS[cursor].id
        : GROUP_COLORS[cursor % GROUP_COLORS.length].id;
    group.color = color;
    used.add(color);
    cursor += 1;
  }
}

/** パウチ gray・グループ色の明示を保証する */
export function normalizeSheetColors(sheet: Sheet): Sheet {
  const pouches: Pouch[] = sheet.pouches.map(pouch => ({
    ...pouch,
    color: isNonEmptyColor(pouch.color) ? pouch.color : DEFAULT_POUCH_COLOR,
  }));
  const groups = assignGroupColors(sheet.groups);
  return { ...sheet, pouches, groups };
}
